import { useMemo, useRef, useState } from "react";
import { CalendarClock, CircleHelp, MoreHorizontal } from "lucide-react";
import { answerSubscriptionCancel, setChargeOccurrence, splitChargeFrom, useAction } from "wasp/client/operations";
import useTranslation from "../../client/hooks/useTranslation";
import { Button } from "../../client/components/ui/button";
import { Input } from "../../client/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../../client/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../client/components/ui/dropdown-menu";
import { toast } from "../../client/hooks/use-toast";
import type { LiteFormat } from "../../lite/lib/format";
import { cancelQuestions, pendingConfirmations, upcomingInstances, type ReviewCharge, type ReviewRow } from "../chargeReview";

/** Prélèvements récurrents : questions de résiliation, échéances passées à
 *  confirmer, puis les 30 prochains jours avec leurs actions. Partagé par le
 *  Lite (UpcomingCard) et l'Advanced (widget propfirm-upcoming-charges). */

const SUBSCRIPTION_SOURCE = "prop_firm_subscription";

export interface PanelCharge extends ReviewCharge {
  account?: { id: string; name: string } | null;
}

type AmountEdit<C extends PanelCharge> = { mode: "one" | "from"; row: ReviewRow<C> };

export function UpcomingChargesPanel<C extends PanelCharge>({
  charges, fmt, accountName, readOnly = false,
}: {
  charges: C[];
  fmt: LiteFormat;
  accountName: (c: C) => string;
  /** Vue mentor ou aperçu de layout partagé : affichage seul, aucune action. */
  readOnly?: boolean;
}) {
  const t = useTranslation().lite.pfm.upcoming;
  const setOccurrence = useAction(setChargeOccurrence);
  const split = useAction(splitChargeFrom);
  const answer = useAction(answerSubscriptionCancel);
  const [edit, setEdit] = useState<AmountEdit<C> | null>(null);
  const [amount, setAmount] = useState("");
  // Une action à la fois : un double clic sur « à partir de cette échéance »
  // découperait deux fois la même charge (le serveur répond 409 au second).
  // La ref garde (deux clics dans le même rendu), l'état désactive les boutons.
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const questions = useMemo(() => cancelQuestions(charges), [charges]);
  const pending = useMemo(() => pendingConfirmations(charges), [charges]);
  const upcoming = useMemo(() => upcomingInstances(charges), [charges]);

  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    try {
      await fn();
      return true;
    } catch {
      toast({ title: t.error, variant: "destructive" });
      return false;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const mark = (row: ReviewRow<C>, status: "skipped" | "confirmed" | null) =>
    run(() => setOccurrence({ chargeId: row.charge.id, dueDate: row.instance.key, status }));
  const openEdit = (mode: "one" | "from", row: ReviewRow<C>) => {
    setAmount(String(row.instance.amount || row.charge.amount));
    setEdit({ mode, row });
  };
  const saveEdit = async () => {
    if (!edit) return;
    const value = Number(amount.replace(",", "."));
    if (!(value > 0)) return;
    const { row, mode } = edit;
    const ok = await run(() => mode === "one"
      ? setOccurrence({ chargeId: row.charge.id, dueDate: row.instance.key, status: "overridden", amount: value })
      : split({ chargeId: row.charge.id, dueDate: row.instance.key, amount: value }));
    // Échec : la boîte reste ouverte, le montant saisi n'est pas perdu.
    if (ok) setEdit(null);
  };
  const subtitle = (row: ReviewRow<C>) => `${fmt.dateLong(row.instance.date)} · ${accountName(row.charge)}`;

  return (
    <div className="space-y-4">
      {questions.map(c => (
        <div key={`q-${c.id}`} className="rounded-xl border border-warning/40 bg-warning/10 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <CircleHelp size={16} className="text-warning shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm">{t.cancelQuestion(accountName(c))}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.cancelHint}</p>
              {!readOnly && <div className="flex gap-2 mt-2">
                <Button size="sm" disabled={busy} onClick={() => run(() => answer({ chargeId: c.id, answer: "cancelled" }))}>{t.cancelled}</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => answer({ chargeId: c.id, answer: "kept" }))}>{t.keep}</Button>
              </div>}
            </div>
          </div>
        </div>
      ))}

      {pending.length > 0 && (
        <div className="space-y-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t.toConfirmTitle}</p>
            <p className="text-xs text-muted-foreground">{t.toConfirmHint}</p>
          </div>
          {pending.map(row => (
            <div key={`p-${row.charge.id}-${row.instance.key}`} className="rounded-xl border border-border px-3 py-2.5">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{row.charge.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{subtitle(row)}</div>
                </div>
                <span className="font-mono tabular-nums text-sm text-pnlNegative">{fmt.money(-row.instance.amount)}</span>
              </div>
              {!readOnly && <div className="flex flex-wrap gap-2 mt-2">
                <Button size="sm" variant="outline" disabled={busy} onClick={() => mark(row, "confirmed")}>{t.paid}</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => mark(row, "skipped")}>{t.notPaid}</Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => openEdit("one", row)}>{t.otherAmount}</Button>
              </div>}
            </div>
          ))}
        </div>
      )}

      {upcoming.length ? (
        <div className="space-y-2 max-h-[260px] overflow-y-auto">
          {upcoming.map(row => {
            const skipped = row.instance.status === "skipped";
            const isSub = row.charge.source === SUBSCRIPTION_SOURCE;
            return (
              <div key={`u-${row.charge.id}-${row.instance.key}`} className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5">
                <CalendarClock size={16} className={skipped ? "text-muted-foreground shrink-0" : "text-warning shrink-0"} />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm truncate ${skipped ? "line-through text-muted-foreground" : ""}`}>{row.charge.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {subtitle(row)}
                    {row.instance.status === "overridden" && ` · ${t.modified}`}
                    {skipped && ` · ${t.skipped}`}
                  </div>
                </div>
                <span className={`font-mono tabular-nums text-sm ${skipped ? "text-muted-foreground" : "text-pnlNegative"}`}>
                  {fmt.money(-row.instance.amount)}
                </span>
                {!readOnly && <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label={t.actions} disabled={busy}>
                      <MoreHorizontal size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {skipped ? (
                      <DropdownMenuItem onClick={() => mark(row, null)}>{t.restore}</DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={() => openEdit("one", row)}>{t.editOne}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit("from", row)}>{t.editFrom}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => mark(row, "skipped")}>{t.skip}</DropdownMenuItem>
                      </>
                    )}
                    {isSub && (
                      <DropdownMenuItem onClick={() => run(() => answer({ chargeId: row.charge.id, answer: "cancelled" }))}>
                        {t.cancelled}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>}
              </div>
            );
          })}
        </div>
      ) : <p className="text-sm text-muted-foreground">{t.none}</p>}

      <Dialog open={!!edit} onOpenChange={open => { if (!open && !busy) setEdit(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit?.mode === "from" ? t.editFromTitle : t.editOneTitle}</DialogTitle>
            {edit?.mode === "from" && <DialogDescription>{t.editFromHint}</DialogDescription>}
          </DialogHeader>
          <label className="text-sm space-y-1 block">
            <span className="text-muted-foreground">{t.amount}</span>
            <Input inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} className="font-mono" />
          </label>
          <DialogFooter>
            <Button variant="ghost" disabled={busy} onClick={() => setEdit(null)}>{t.cancel}</Button>
            <Button onClick={saveEdit} disabled={busy}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
